import { v4 as genUuid } from "uuid";

import {
  ContentType,
  ItemType,
  Item,
  ItemDisplayProperties,
  Asset,
} from "@thenounproject/lingo-core";

import {
  type InsertPosition,
  type Upload,
  type UploadAction,
  getItemData,
  generateInsertPositionForUploads,
} from "@actions/uploads";
import { createAsyncAction } from "../actionCreators";
import { fetchContentTypes } from "../useFetchContentTypes";
import { enqueueUpload } from "../uploads/enqueueUpload";
import { processExternalFile } from "../assets/processExternalFile";

export enum AssetSourceVendors {
  Figma = "figma",
  GoogleFonts = "google-fonts",
  GoogleDrive = "google-drive",
  Dropbox = "dropbox",
}

export type ExternalSource = {
  source: AssetSourceVendors;
  name?: string;
  url?: string;
  authToken?: string;
  fileSize?: number;
};

type Args = {
  sources: ExternalSource[];
  insertPosition: InsertPosition;
  itemType?: ItemType;
  item?: Item;
  displayProperties?: ItemDisplayProperties;
  previewBackground?: Asset["meta"]["backgroundColor"];
};

type CreateAssetPayload = {
  auth_token?: string;
  source?: string;
  type?: string;
  name?: string;
  source_url?: string;
  source_headers?: Record<string, string>;
  space_id?: number;
  item?: {
    uuid?: string;
    kit_uuid?: string;
    type?: string;
  };
  meta: {
    background_color?: string;
    figma?: { url: string };
    font?: { font_name?: string; extension?: string };
  };
};

const [useCreateAssetsFromSource, createAssetsFromSource] = createAsyncAction(
  "assets/createFromSource",
  async (
    {
      sources,
      insertPosition: startingInsertPosition,
      itemType = ItemType.asset,
      item,
      displayProperties,
      previewBackground,
    }: Args,
    thunkApi
  ) => {
    const contentTypes = await fetchContentTypes.lazy(thunkApi, {
      itemType: item?.type ?? itemType,
      assetType: item?.asset?.type,
    });
    const batchId = genUuid();
    const insertPosition = generateInsertPositionForUploads(sources.length, startingInsertPosition);

    // Queue the uploads
    sources
      .map(source =>
        generateUploadAction(
          source,
          insertPosition,
          itemType,
          batchId,
          contentTypes,
          item,
          displayProperties,
          previewBackground
        )
      )
      .forEach(uploadAction => thunkApi.dispatch(enqueueUpload(uploadAction)));
  }
);

export default useCreateAssetsFromSource;
export { createAssetsFromSource };

const generateUploadAction = (
  source: ExternalSource,
  insertPosition: InsertPosition,
  itemType: ItemType,
  batchId: string,
  contentTypes: ContentType[],
  existingItem?: Item,
  displayProperties?: ItemDisplayProperties,
  previewBackground?: Asset["meta"]["backgroundColor"]
): UploadAction => {
  const { item: itemData } = getItemData(insertPosition);

  itemData.display_properties = displayProperties;

  // If an existing item is provided, use its structure (like useCreateItemAsset does)
  const item = existingItem
    ? { uuid: existingItem.uuid, kit_uuid: existingItem.kitUuid, type: existingItem.type }
    : itemData;

  const upload: Upload = {
    id: genUuid().toUpperCase(),
    /**
     * If no batch ID is provided, it's likely a one-off upload.
     * This is currently used by the Figma modal to either insert a
     * single Figma frame or fetch the contents of an entire page.
     */
    batchId: batchId || undefined,
    status: "pending",
    type: source.source,
    name: source.name,
    size: source.fileSize ?? 0,
    insertPosition,
    isAsset: itemType === ItemType.asset,
  };

  const asset: CreateAssetPayload = {
    auth_token: source.authToken,
    meta: {
      background_color: previewBackground || undefined,
    },
    space_id: insertPosition.spaceId,
    item,
  };

  switch (source.source) {
    case AssetSourceVendors.Figma:
      asset.source = source.source;
      upload.type = "Figma asset";
      upload.name = source.name;
      asset.meta.figma = { url: source.url };
      break;
    case AssetSourceVendors.GoogleFonts:
      asset.source = source.source;
      upload.type = "Google font";
      upload.name = source.name;
      asset.meta.font = { font_name: source.name };
      break;
    case AssetSourceVendors.GoogleDrive:
      upload.type = "Google Drive";
      processExternalFile(source, upload, asset, contentTypes, { setAssetName: true });
      asset.source_headers = { Authorization: `Bearer ${source.authToken}` };
      break;
    case AssetSourceVendors.Dropbox:
      upload.type = "Dropbox";
      processExternalFile(source, upload, asset, contentTypes, { setAssetName: true });
      break;
  }
  return {
    upload,
    requestData: {
      data: asset,
      endpoint: "assets",
      method: "POST",
    },
  };
};
